import React, { useState } from 'react';
import { Code, Cpu, Sparkles, Home, Search, Terminal } from 'lucide-react';
import { portfolioData } from '../data/portfolioData';

export default function TechStack() {
  const { techStack } = portfolioData;
  const [searchQuery, setSearchQuery] = useState('');

  const getCategoryIcon = (iconName) => {
    switch (iconName) {
      case 'Code': return <Code className="w-5 h-5 text-blue-400" />;
      case 'Cpu': return <Cpu className="w-5 h-5 text-amber-400" />;
      case 'Sparkles': return <Sparkles className="w-5 h-5 text-pink-400" />;
      case 'Home': return <Home className="w-5 h-5 text-emerald-400" />;
      default: return <Terminal className="w-5 h-5 text-brand-bronze" />;
    }
  };

  return (
    <section id="tech" className="py-20 bg-slate-50 dark:bg-slate-950/80 border-t border-slate-200 dark:border-slate-800 relative transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-bronze/10 border border-brand-bronze/30 text-brand-bronze text-xs font-mono">
              <Terminal className="w-3.5 h-3.5" /> TIDY & ORGANIZED TOOLKIT
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white">
              Tech Stack & Hardware Tools
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-base max-w-xl">
              Curated libraries, frameworks, automation tools, and physical setup standards.
            </p>
          </div>

          {/* Search Input */}
          <div className="relative w-full md:w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search tools (e.g. React, Docker)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm text-slate-900 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-brand-bronze transition-colors shadow-sm dark:shadow-none"
            />
          </div>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {techStack.map((group) => {
            const filteredSkills = group.skills.filter(s => 
              s.toLowerCase().includes(searchQuery.toLowerCase())
            );

            if (searchQuery && filteredSkills.length === 0) return null;

            return (
              <div 
                key={group.category}
                className="rounded-3xl bg-white/90 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 p-6 space-y-4 hover:border-slate-300 dark:hover:border-slate-700 transition-all flex flex-col justify-between shadow-lg dark:shadow-xl"
              >
                <div className="space-y-4">
                  <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 pb-3">
                    <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-800">
                      {getCategoryIcon(group.icon)}
                    </div>
                    <h3 className="font-bold text-base text-slate-900 dark:text-white">{group.category}</h3>
                  </div>

                  <ul className="space-y-2">
                    {filteredSkills.map((skill) => (
                      <li 
                        key={skill}
                        className="text-xs font-mono px-3 py-2 rounded-xl bg-slate-100/80 dark:bg-slate-950/70 border border-slate-200 dark:border-slate-800/80 text-slate-800 dark:text-slate-300 flex items-center justify-between hover:border-brand-bronze/40 transition-colors"
                      >
                        <span>{skill}</span>
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-bronze"></span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-3 border-t border-slate-200 dark:border-slate-800/60 text-[10px] font-mono text-slate-500 text-right">
                  {filteredSkills.length} Verified Skills
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
